import { AlertTriangle, ShieldAlert } from "lucide-react";

import { CoachingExplanation }
  from "@/components/coaching/CoachingExplanation";
import { renderMessage } from "@/lib/coachingMessageRegistry";

/**
 * Highest-priority banner. Renders whenever training is suspended,
 * professional review is required, Chat 2 reassessment is required, or
 * safety confidence is insufficient. Never rendered ahead of the
 * primary training card unless one of these conditions holds.
 */
export const SafetyBanner = ({ safety, safetyExplanation,
                                        reassessmentRequired }) => {
  const isSuspended = Boolean(safety?.training_suspended);
  const professionalReview = Boolean(safety?.professional_review_required);
  const reassessment = Boolean(reassessmentRequired
                                        || safety?.reassessment_required);
  if (!isSuspended && !professionalReview && !reassessment) return null;

  const critical = isSuspended || professionalReview;
  const roleAttr = critical ? "alert" : "status";
  const title = renderMessage(
    critical ? "COACH_TITLE_SAFETY_SUSPENSION"
                : "COACH_TITLE_CHAT2_REASSESSMENT",
    {}).text;

  const badgeClass = critical
    ? "bg-analytics-red/15 border-analytics-red text-analytics-red"
    : "bg-analytics-amber/15 border-analytics-amber text-analytics-amber";
  const Icon = critical ? ShieldAlert : AlertTriangle;

  return (
    <section
      role={roleAttr}
      aria-live={critical ? "assertive" : "polite"}
      data-testid="coaching-safety-banner"
      className={`app-card p-5 ${badgeClass}`}
    >
      <div className="flex items-start gap-3">
        <Icon className="w-5 h-5 mt-0.5 shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-heading text-lg tracking-wider uppercase">
              {title}
            </h2>
            <span className="text-[10px] tracking-widest uppercase border border-current px-2 py-0.5 rounded"
                    data-testid="safety-severity-label">
              {critical ? "Critical" : "Important"}
            </span>
          </div>
          {safetyExplanation && (
            <div className="mt-3">
              <CoachingExplanation
                explanation={safetyExplanation}
                showMeta
                testId="safety-explanation"
              />
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2 text-[11px] uppercase tracking-widest">
            {professionalReview && (
              <span className="border border-current px-3 py-1.5 rounded"
                      data-testid="action-professional-review">
                Seek professional review
              </span>
            )}
            {reassessment && (
              <span className="border border-current px-3 py-1.5 rounded"
                      data-testid="action-request-reassessment">
                Request reassessment
              </span>
            )}
            <span className="border border-current/50 px-3 py-1.5 rounded opacity-80"
                    data-testid="action-contact-support">
              Contact support
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};
